import {useEffect, useMemo, useRef} from 'react'

import {MemexColumnDataType, SystemColumnId} from '../../api/columns/contracts/memex-column'
import type {UpdateColumnValueAction} from '../../api/columns/contracts/storage'
import {IssueState, type ParentIssue} from '../../api/common-contracts'
import type {GroupingMetadataWithSource} from '../../features/grouping/types'
import {shortcutFromEvent, SHORTCUTS} from '../../helpers/keyboard-shortcuts'
import type {Row} from '../../hooks/tanstack-table/helpers/types'
import {useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import {IssueModel} from '../../models/memex-item-model'
import {handleKeyboardNavigation, suppressEvents, suppressOmnibarEvents} from '../../navigation/keyboard'
import {Omnibar, type OmnibarRef} from '../omnibar/omnibar'
import {CELL_HEIGHT} from './constants'
import {focusAddSubIssueButton, TableFocusType, useStableTableNavigation} from './navigation'
import styles from './sub-issue-omnibar-row.module.css'
import type {TableDataType} from './table-data-type'

type SubIssueOmnibarRowProps = {
  previousItemId: number
  parentRow: Row<TableDataType>
  onClose: () => void
  subIssueDepth?: number
}

/**
 * Wrapper component for rendering an Omnibar inline below a table row.
 * Handles closing the omnibar on blur or escape key.
 */
export function SubIssueOmnibarRow({previousItemId, parentRow, onClose, subIssueDepth = 0}: SubIssueOmnibarRowProps) {
  const omnibarRef = useRef<OmnibarRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const {getVisibleLeafColumns} = useTableInstance()
  const {navigationDispatch, stateRef} = useStableTableNavigation()

  // Calculate the width of columns before the Title column to properly indent the omnibar
  const titleColumnOffset = useMemo(() => {
    // Calculate offset based on sub-issue depth to add to the total indentation
    // Each level of depth adds 24px of indentation
    const subIssueDepthOffset = (subIssueDepth + 1) * 24
    const visibleColumns = getVisibleLeafColumns()
    const titleColumnIndex = visibleColumns.findIndex(c => c.id === 'Title')
    if (titleColumnIndex === -1) return subIssueDepthOffset

    const columnsBeforeTitle = visibleColumns.slice(0, titleColumnIndex)
    const columnOffset = columnsBeforeTitle.reduce((acc, column) => acc + column.getSize(), 0)
    return columnOffset + subIssueDepthOffset
  }, [getVisibleLeafColumns, subIssueDepth])

  // Create an update column value action, which will automatically set the parent issue when creating a new issue or
  // adding an existing issue as a sub-issue.
  const parentIssueUpdateAction: UpdateColumnValueAction | undefined = useMemo(() => {
    const parentItem = parentRow.original
    if (!(parentItem instanceof IssueModel)) return undefined

    const nwo = parentItem.getNameWithOwner()
    if (!nwo || nwo.length !== 2) return undefined

    const [owner, repository] = nwo
    if (!owner || !repository) return undefined

    // Global Relay ID is required in order to create a sub-issue, because it uses the GraphQL API
    const globalRelayId = parentItem.content.globalRelayId
    if (!globalRelayId) return undefined

    // Construct a ParentIssue object from the parent row's data
    const parentIssue: ParentIssue = {
      id: parentItem.content.id,
      globalRelayId,
      number: parentItem.getItemNumber(),
      // This really can't ever be undefined, but our types don't accurately represent reality so we provide a fallback
      // just in case here.
      state: parentItem.state ?? IssueState.Open,
      stateReason: parentItem.stateReason,
      nwoReference: parentItem.getNameWithOwnerReference(),
      title: parentItem.getRawTitle(),
      titleHtml: parentItem.getHtmlTitle(),
      url: parentItem.content.url,
      repository,
      owner,
      updatedAt: parentItem.updatedAt || new Date().toISOString(),
    }

    return {
      dataType: MemexColumnDataType.ParentIssue,
      value: parentIssue,
    }
  }, [parentRow])

  const updateColumnActions: Array<UpdateColumnValueAction> = useMemo(() => {
    const actions = []

    if (parentIssueUpdateAction) {
      actions.push(parentIssueUpdateAction)
    }

    return actions
  }, [parentIssueUpdateAction])

  // Create grouping metadata on the client in order to properly apply the parent issue when adding a new sub-issue.
  // This will mimic the behavior of grouping by parent issue such that we can warn users if they're changing the
  // parent issue of an existing sub-issue.
  const groupingMetadata: GroupingMetadataWithSource | undefined = useMemo(() => {
    const parentIssue = parentIssueUpdateAction?.value
    if (!parentIssue) return undefined

    return {
      value: parentIssue.nwoReference,
      sourceObject: {
        kind: 'group' as const,
        dataType: MemexColumnDataType.ParentIssue,
        value: parentIssue,
      },
    } satisfies GroupingMetadataWithSource
  }, [parentIssueUpdateAction?.value])

  useEffect(() => {
    // Focus the omnibar when it opens
    requestAnimationFrame(() => {
      // TODO: Use table navigation reducer instead of manually focusing here, in order to support keyboard navigation.
      omnibarRef.current?.focus()
    })
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const shortcut = shortcutFromEvent(event)

    // Don't allow left/right movement, just stay in place.
    if (
      shortcut === SHORTCUTS.META_ARROW_LEFT ||
      shortcut === SHORTCUTS.ARROW_LEFT ||
      shortcut === SHORTCUTS.META_ARROW_RIGHT ||
      shortcut === SHORTCUTS.ARROW_RIGHT
    ) {
      return
    }

    if (shortcut === SHORTCUTS.ESCAPE) {
      event.preventDefault()
      suppressEvents(event)
      onClose()
      navigationDispatch(focusAddSubIssueButton(parentRow.id, SystemColumnId.Title))
      return
    }

    const result = handleKeyboardNavigation(navigationDispatch, event)

    if (result.action) {
      suppressOmnibarEvents(event, result.keyAsShortcut)
      onClose()
    }
  }

  useEffect(() => {
    // Whether or not the virtual focus is set to the omnibar
    const isMemexFocusInOmnibar = stateRef.current?.focus?.type === TableFocusType.SUB_ISSUE_OMNIBAR
    // Whether or not the actual DOM focus is inside the omnibar element (allows focusing the plus button without closing)
    const isFocusInOmnibar = containerRef.current?.contains(document.activeElement)
    // If the focus moves to something else, ensure it gets closed
    if (!isMemexFocusInOmnibar && !isFocusInOmnibar) {
      onClose()
    }
  }, [onClose, stateRef])

  return (
    <div
      style={{
        height: `${CELL_HEIGHT}px`,
        paddingLeft: `${titleColumnOffset}px`,
      }}
      className={styles.InlineOmnibar}
      ref={containerRef}
    >
      <Omnibar
        ref={omnibarRef}
        defaultPlaceholder="Add a sub-issue"
        newItemAttributes={{
          previousItemId,
          isCreatingSubIssue: true,
          updateColumnActions,
          // Set the group ID to the parent issue as if we were grouped by parent issue
          groupId: groupingMetadata?.value,
        }}
        groupingMetadata={groupingMetadata}
        onKeyDown={handleKeyDown}
      />
    </div>
  )
}

try{ SubIssueOmnibarRow.displayName ||= 'SubIssueOmnibarRow' } catch {}